import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { PageHeader } from '@/components/PageHeader';
import { ArrowLeft, Loader2, ShoppingCart, Plus, Trash2, Check, ChevronsUpDown, TrendingUp, TrendingDown, Eye, AlertCircle, DollarSign, CreditCard, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getProducts } from '@/integrations/supabase/products';
import { createOrder } from '@/integrations/supabase/orders';
import { getCurrentEmpresaId } from '@/integrations/supabase/auth-utils';
import { getEmpresaConfig } from '@/integrations/supabase/empresa-config';
import { supabase } from '@/integrations/supabase/client';
import { startOfDay, endOfDay, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DialogEmitirNota } from '@/components/DialogEmitirNota';
import { DialogSelecionarFormaEConta } from '@/components/DialogSelecionarFormaEConta';
import { DialogAbrirFecharCaixa } from '@/components/DialogAbrirFecharCaixa';
import { focusNFService } from '@/integrations/focusnf/service';
import { ORDER_STATUS } from '@/utils/statusConstants';
import { formatCurrency } from '@/utils/formatCurrency';
import { calcularSaldoAtual, calcularSaldoRealAtualAsync } from '@/utils/financialUtils';
import { buscarCaixaAbertoHoje, CaixaSession } from '@/integrations/supabase/caixa-sessions';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default function PDV() {
  const navigate = useNavigate();
  const [carrinho, setCarrinho] = useState<any[]>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [metodo, setMetodo] = useState<'pix' | 'cartao' | 'dinheiro'>('pix');
  const [valorPago, setValorPago] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState({ vendas: 0, itens: 0, nfce: 0 });
  const [ultimoPedido, setUltimoPedido] = useState<{ id: string; code: string; total: number } | null>(null);
  const [ultimosItensVenda, setUltimosItensVenda] = useState<any[]>([]);
  const [showReciboDialog, setShowReciboDialog] = useState(false);
  const [showNfceDialog, setShowNfceDialog] = useState(false);
  const [emitindoNota, setEmitindoNota] = useState(false);
  const [emitirNfceAutomaticamente, setEmitirNfceAutomaticamente] = useState(false);
  const [usarNfceRapida, setUsarNfceRapida] = useState(true);
  const [saldoCaixa, setSaldoCaixa] = useState(0);
  const [pagamentosPorMetodo, setPagamentosPorMetodo] = useState({ pix: 0, dinheiro: 0, cartao: 0 });
  const [saldoFluxo, setSaldoFluxo] = useState(0);
  const [showSelecionarFormaModal, setShowSelecionarFormaModal] = useState(false);
  const [formaIdSelecionada, setFormaIdSelecionada] = useState<string | null>(null);
  const [contaIdSelecionada, setContaIdSelecionada] = useState<string | null>(null);
  const [isDialogCaixaOpen, setIsDialogCaixaOpen] = useState(false);
  const [modoCaixa, setModoCaixa] = useState<'abrir' | 'fechar'>('abrir');
  const [caixaAtual, setCaixaAtual] = useState<CaixaSession | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    try {
      const valorSalvo = localStorage.getItem('pdv.emitirNfceAutomaticamente');
      if (valorSalvo !== null) {
        setEmitirNfceAutomaticamente(valorSalvo === 'true');
      }

      const modoRapidoSalvo = localStorage.getItem('pdv.usarNfceRapida');
      if (modoRapidoSalvo !== null) {
        setUsarNfceRapida(modoRapidoSalvo === 'true');
      }
    } catch (error) {
      console.warn('Não foi possível ler preferência do PDV no navegador:', error);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('pdv.emitirNfceAutomaticamente', String(emitirNfceAutomaticamente));
    } catch (error) {
      console.warn('Não foi possível salvar preferência do PDV no navegador:', error);
    }
  }, [emitirNfceAutomaticamente]);

  useEffect(() => {
    try {
      localStorage.setItem('pdv.usarNfceRapida', String(usarNfceRapida));
    } catch (error) {
      console.warn('Não foi possível salvar modo rápido da NFC-e no navegador:', error);
    }
  }, [usarNfceRapida]);

  // Buscar produtos
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: getProducts,
  });

  // Buscar dados do dashboard
  useQuery({
    queryKey: ['pdv-dashboard'],
    queryFn: async () => {
      const empresaId = await getCurrentEmpresaId();
      const hoje = new Date();
      const inicio = startOfDay(hoje).toISOString();
      const fim = endOfDay(hoje).toISOString();

      const { data: orders } = await supabase
        .from('atelie_orders')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('type', 'PDV')
        .gte('created_at', inicio)
        .lte('created_at', fim);

      const vendas = (orders || []).reduce((acc, o) => acc + (Number(o.value) || 0), 0);
      const itens = (orders || []).reduce((acc, o) => acc + (Number(o.quantity) || 0), 0);

      setDashboardData({ vendas, itens, nfce: (orders || []).length });
      return { vendas, itens };
    },
    refetchInterval: 10000,
  });

  // Buscar saldo de caixa (usando função oficial única)
  useQuery({
    queryKey: ['pdv-saldo-caixa'],
    queryFn: async () => {
      try {
        const resultado = await calcularSaldoRealAtualAsync();
        setSaldoCaixa(resultado.saldoAtual);
        console.log('💰 Saldo de caixa sincronizado:', resultado.saldoAtual);
        return resultado.saldoAtual;
      } catch (error) {
        console.warn('Erro ao calcular saldo de caixa:', error);
        setSaldoCaixa(0);
        return 0;
      }
    },
    refetchInterval: 10000,
  });

  // Buscar pagamentos por método
  useQuery({
    queryKey: ['pdv-pagamentos-metodo'],
    queryFn: async () => {
      const empresaId = await getCurrentEmpresaId();
      const hoje = new Date();
      const inicio = startOfDay(hoje).toISOString();
      const fim = endOfDay(hoje).toISOString();

      try {
        const { data: orders } = await supabase
          .from('atelie_orders')
          .select('value, metodo')
          .eq('empresa_id', empresaId)
          .eq('type', 'PDV')
          .gte('created_at', inicio)
          .lte('created_at', fim);

        const pagamentos = {
          pix: 0,
          dinheiro: 0,
          cartao: 0
        };

        (orders || []).forEach((order: any) => {
          const valor = Number(order.value) || 0;
          if (order.metodo === 'pix') pagamentos.pix += valor;
          else if (order.metodo === 'dinheiro') pagamentos.dinheiro += valor;
          else if (order.metodo === 'cartao') pagamentos.cartao += valor;
        });

        setPagamentosPorMetodo(pagamentos);
        return pagamentos;
      } catch (error) {
        console.warn('Erro ao buscar pagamentos por método:', error);
        return { pix: 0, dinheiro: 0, cartao: 0 };
      }
    },
    refetchInterval: 10000,
  });

  // Buscar caixa aberto de hoje
  useQuery({
    queryKey: ['pdv-caixa-aberto'],
    queryFn: async () => {
      const caixa = await buscarCaixaAbertoHoje();
      setCaixaAtual(caixa);
      return caixa;
    },
    refetchInterval: 15000,
  });

  // Buscar saldo do Fluxo de Caixa para reconciliação
  useQuery({
    queryKey: ['pdv-saldo-fluxo'],
    queryFn: async () => {
      const empresaId = await getCurrentEmpresaId();

      try {
        const config = await getEmpresaConfig(empresaId);

        // Buscar contas a receber e pagar (para hoje)
        const hoje = new Date();
        const { data: contasReceber } = await supabase
          .from('contas_receber')
          .select('valor_recebido, data_recebimento')
          .eq('empresa_id', empresaId)
          .eq('status', 'recebido')
          .like('data_recebimento', `${format(hoje, 'yyyy-MM-dd')}%`);

        const { data: contasPagar } = await supabase
          .from('contas_pagar')
          .select('valor_pago, data_pagamento')
          .eq('empresa_id', empresaId)
          .eq('status', 'pago')
          .like('data_pagamento', `${format(hoje, 'yyyy-MM-dd')}%`);

        const receitasRealizadas = (contasReceber || []).reduce((acc, conta) => acc + (Number(conta.valor_recebido) || 0), 0);
        const despesasRealizadas = (contasPagar || []).reduce((acc, conta) => acc + (Number(conta.valor_pago) || 0), 0);

        const saldo = (config?.saldo_inicial || 0) + receitasRealizadas - despesasRealizadas;
        setSaldoFluxo(saldo);
        return saldo;
      } catch (error) {
        console.warn('Erro ao buscar saldo do fluxo:', error);
        return 0;
      }
    },
    refetchInterval: 10000,
  });

  // Normalizar busca
  const normalizeSearch = (value: string) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/\s+/g, ' ')
      .trim();

  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return products;
    const search = normalizeSearch(searchTerm);
    return products.filter(p => normalizeSearch(`${p.name} ${p.type || ''}`).includes(search));
  }, [products, searchTerm]);

  const total = useMemo(() => {
    return carrinho.reduce((acc, p) => acc + p.price * p.quantity, 0);
  }, [carrinho]);

  const handleAdicionarProduto = (produtoId: string) => {
    const produto = products.find(p => p.id === produtoId);
    if (!produto) return;

    const existe = carrinho.find(p => p.id === produtoId && !p.isCustom);
    if (existe) {
      setCarrinho(prev => prev.map(p =>
        p.id === produtoId ? { ...p, quantity: p.quantity + 1 } : p
      ));
    } else {
      setCarrinho(prev => [...prev, {
        id: produto.id,
        name: produto.name,
        price: produto.unit_price,
        quantity: 1,
        isCustom: false
      }]);
    }

    setSelectedProduct('');
    setSearchTerm('');
    setPopoverOpen(false);
    toast.success(`${produto.name} adicionado!`);
  };

  const handleAdicionarCustomizado = () => {
    if (!customName.trim()) {
      toast.error('Nome obrigatório');
      return;
    }

    const preco = parseFloat(customPrice) || 0;
    if (preco <= 0) {
      toast.error('Preço deve ser > 0');
      return;
    }

    setCarrinho(prev => [...prev, {
      id: `custom-${Date.now()}`,
      name: customName,
      price: preco,
      quantity: 1,
      isCustom: true
    }]);

    setCustomName('');
    setCustomPrice('');
    toast.success('Produto adicionado!');
  };

  const handleRemover = (id: string) => {
    setCarrinho(prev => prev.filter(p => p.id !== id));
  };

  const handleAtualizarQtd = (id: string, novaQtd: number) => {
    if (novaQtd <= 0) {
      handleRemover(id);
      return;
    }
    setCarrinho(prev => prev.map(p => p.id === id ? { ...p, quantity: novaQtd } : p));
  };

  const resetarVenda = () => {
    setCarrinho([]);
    setCustomName('');
    setCustomPrice('');
    setMetodo('pix');
    setValorPago('');
    setSelectedProduct('');
    setSearchTerm('');
    setShowDialog(false);
    setShowNfceDialog(false);
    setShowReciboDialog(false);
  };

  const handleFinalizarVenda = async () => {
    if (carrinho.length === 0) {
      toast.error('Carrinho vazio');
      return;
    }

    // Abrir dialog para selecionar forma e conta
    setShowSelecionarFormaModal(true);
  };

  const handleConfirmarVendaComFormaEConta = async (formaId: string | null, contaId: string | null) => {
    setFormaIdSelecionada(formaId);
    setContaIdSelecionada(contaId);
    setShowSelecionarFormaModal(false);

    setIsProcessing(true);

    try {
      const empresaId = await getCurrentEmpresaId();
      if (!empresaId) throw new Error('Empresa não identificada');

      // 1. Gerar código único
      const agora = new Date();
      const dia = String(agora.getDate()).padStart(2, '0');
      const mes = String(agora.getMonth() + 1).padStart(2, '0');
      const ano = String(agora.getFullYear()).slice(-2);
      const hora = String(agora.getHours()).padStart(2, '0');
      const minuto = String(agora.getMinutes()).padStart(2, '0');
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  // Banco em produção possui code com limite de 20 chars em alguns ambientes.
  // Exemplo final: PDV-300326-2359-AB12 (20 chars)
  const codigoOrdem = `PDV-${dia}${mes}${ano}-${hora}${minuto}-${random}`.slice(0, 20);

  const itensVendaAtual = [...carrinho];

  // 2. Criar venda em orders
      const descricao = carrinho
        .map(p => `${p.name} (${p.quantity}x R$ ${p.price.toFixed(2)})`)
        .join(' | ');

      // Evita violar possíveis constraints de tipo no banco
      // Reaproveita tipos já usados em NovoPedido.tsx
      const tipoPedido = carrinho.some((p) => p.isCustom) ? 'personalizado' : 'catalogo';

      // Preparar produtos para a baixa automática de estoque
      const produtosParaBaixar = carrinho
        .filter(p => !p.isCustom)
        .map(p => ({ id: p.id, quantity: p.quantity }));

      const resultado = await createOrder({
        customer_name: 'Consumidor Final',
        description: descricao,
        value: total,
        status: ORDER_STATUS.ENTREGUE,
        type: tipoPedido,
        paid: 1,
        code: codigoOrdem,
        products: produtosParaBaixar.length > 0 ? produtosParaBaixar : undefined
      });

      if (!resultado.ok || !resultado.id) {
        throw new Error(resultado.error || 'Erro ao registrar venda');
      }

      console.log('✅ Venda registrada:', resultado.id);
      console.log('📦 Estoque decrementado automaticamente para', produtosParaBaixar.length, 'produtos');

      // Log de avisos de estoque
      if (resultado.inventoryWarnings && resultado.inventoryWarnings.length > 0) {
        console.warn('⚠️ Avisos de estoque:', resultado.inventoryWarnings);
        resultado.inventoryWarnings.forEach(warning => {
          toast.warning(warning.message);
        });
      }

      // 4. Refetch das queries para atualizar dashboard
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['pdv-dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['pdv-saldo-caixa'] }),
        queryClient.invalidateQueries({ queryKey: ['pdv-pagamentos-metodo'] }),
        queryClient.invalidateQueries({ queryKey: ['pdv-saldo-fluxo'] }),
        queryClient.invalidateQueries({ queryKey: ['products'] })
      ]);

      // 6. Sucesso
      toast.success(`✅ Venda finalizada!\nCódigo: ${codigoOrdem}\nTotal: R$ ${total.toFixed(2)}`, {
        duration: 5000
      });

      setUltimoPedido({ id: resultado.id, code: codigoOrdem, total });
  setUltimosItensVenda(itensVendaAtual);

      // 7. Limpar
      setCarrinho([]);
      setMetodo('pix');
      setValorPago('');
      setShowDialog(false);
      if (emitirNfceAutomaticamente) {
        if (usarNfceRapida) {
          // Modo balcão: sempre tenta emissão rápida, sem abrir modal
          toast.info('Emitindo NFC-e balcão automaticamente...');
          await emitirNfceRapida(codigoOrdem, total, itensVendaAtual);
          // Após a emissão (sucesso ou erro), mostrar recibo
          setShowReciboDialog(true);
        } else {
          // Modo completo: abre modal para preencher dados do cliente
          setShowNfceDialog(true);
          toast.info('Abrindo emissão de NFC-e automaticamente...');
        }
      } else {
        setShowReciboDialog(true);
      }
    } catch (error) {
      console.error('❌ Erro:', error);
      toast.error('Erro ao finalizar venda: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
    } finally {
      setIsProcessing(false);
    }
  };

  const abrirDanfe = (danfeUrl?: string) => {
    if (!danfeUrl) return false;

    const janela = window.open(danfeUrl, '_blank', 'noopener,noreferrer');
    if (!janela) {
      toast.warning('NFC-e autorizada, mas o navegador bloqueou a abertura do DANFE. Libere pop-up para imprimir.');
      return false;
    }

    toast.success('DANFE oficial aberto para impressão.');
    return true;
  };

  const extrairDanfeDaNota = (nota: any): string | undefined => {
    if (!nota) return undefined;

    return (
      nota?.danfe_url ||
      nota?.dados_retornados?.danfe_url ||
      nota?.dados_retornados?.url_danfe ||
      nota?.dados_retornados?.url
    );
  };

  const statusNormalizado = (status?: string) => String(status || '').trim().toLowerCase();

  const aguardarDanfeAutorizado = async (orderCode: string, ref?: string) => {
    const tentativas = 8;

    for (let tentativa = 1; tentativa <= tentativas; tentativa++) {
      if (tentativa > 1) {
        await sleep(2500);
      }

      let notaAtual: any = null;

      if (ref) {
        // Buscar tipo_nota da nota armazenada
        try {
          const empresa_id = await getCurrentEmpresaId();
          const { supabase } = await import('@/integrations/supabase/client');
          const { data: notaExistente } = await supabase
            .from('focusnf_notas')
            .select('tipo_nota')
            .eq('ref', ref)
            .eq('empresa_id', empresa_id || '')
            .single();
          
          const tipoNota = notaExistente?.tipo_nota || 'NFCe';
          const consulta = await focusNFService.consultarNota(ref, true, tipoNota);
          if (consulta.ok && consulta.data) {
            notaAtual = consulta.data;
          }
        } catch (error) {
          // Fallback se não conseguir buscar
          const consulta = await focusNFService.consultarNota(ref, true);
          if (consulta.ok && consulta.data) {
            notaAtual = consulta.data;
          }
        }
      }

      if (!notaAtual) {
        const notas = await focusNFService.listarNotas(orderCode);
        notaAtual = notas?.[0] || null;
      }

      const status = statusNormalizado(notaAtual?.status);
      const danfeUrl = extrairDanfeDaNota(notaAtual);

      if (status === 'autorizado' && danfeUrl) {
        return { ok: true, danfeUrl };
      }

      if (status.startsWith('erro') || status === 'denegado' || status === 'cancelado') {
        return {
          ok: false,
          error: notaAtual?.erro_mensagem || `NFC-e retornou status ${notaAtual?.status || 'de erro'}.`,
        };
      }
    }

    return {
      ok: false,
      error: 'NFC-e enviada, mas ainda em processamento. Consulte novamente em instantes para imprimir o DANFE oficial.',
    };
  };

  const processarPosEmissaoNfce = async (orderCode: string, notaRetorno?: any) => {
    const statusInicial = statusNormalizado(notaRetorno?.status);
    const danfeInicial = extrairDanfeDaNota(notaRetorno);

    if (statusInicial === 'autorizado' && danfeInicial) {
      abrirDanfe(danfeInicial);
      // Fechar dialog de recibo e resetar
      setShowReciboDialog(false);
      resetarVenda();
      return true;
    }

    toast.info('NFC-e enviada para a Sefaz. Aguardando autorização...');
    setShowReciboDialog(false); // Fechar dialog enquanto aguarda

    const resultado = await aguardarDanfeAutorizado(orderCode, notaRetorno?.ref);
    if (resultado.ok) {
      abrirDanfe(resultado.danfeUrl);
      resetarVenda();
      return true;
    }

    toast.warning(resultado.error || 'Não foi possível abrir o DANFE agora.');
    resetarVenda();
    return false;
  };

  const imprimirDanfeUltimaVenda = async () => {
    if (!ultimoPedido?.code) {
      toast.error('Nenhuma venda disponível para imprimir DANFE.');
      return;
    }

    const notas = await focusNFService.listarNotas(ultimoPedido.code);
    const nota = notas?.[0];

    if (!nota) {
      toast.warning('Nenhuma NFC-e encontrada para esta venda. Emita a NFC-e primeiro.');
      return;
    }

    const status = statusNormalizado(nota.status);
    const danfeUrl = extrairDanfeDaNota(nota);

    if (status === 'autorizado' && danfeUrl) {
      abrirDanfe(danfeUrl);
      return;
    }

    const resultado = await aguardarDanfeAutorizado(ultimoPedido.code, nota.ref);
    if (resultado.ok) {
      abrirDanfe(resultado.danfeUrl);
      return;
    }

    toast.warning(resultado.error || 'Ainda não foi possível abrir o DANFE.');
  };

  const imprimirRecibo = () => {
    if (!ultimoPedido) return;

    const dataHora = new Date().toLocaleString('pt-BR');
    const itensHtml = `
      ${ultimosItensVenda.map(p => `
        <tr>
          <td>${p.name}</td>
          <td style="text-align:center;">${p.quantity}</td>
          <td style="text-align:right;">${(p.price * p.quantity).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
        </tr>
      `).join('')}
    `;

    const html = `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Recibo ${ultimoPedido.code}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 16px; }
          h2 { margin: 0 0 12px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          td, th { border-bottom: 1px dashed #ccc; padding: 6px 0; font-size: 12px; }
          .total { font-size: 16px; font-weight: bold; margin-top: 14px; }
          .meta { font-size: 12px; color: #555; margin-top: 8px; }
        </style>
      </head>
      <body>
        <h2>Recibo de Venda - PDV</h2>
        <div class="meta">Código: ${ultimoPedido.code}</div>
        <div class="meta">Data: ${dataHora}</div>
        <div class="meta">Pagamento: ${metodo.toUpperCase()}</div>

        <table>
          <thead>
            <tr>
              <th style="text-align:left;">Item</th>
              <th style="text-align:center;">Qtd</th>
              <th style="text-align:right;">Total</th>
            </tr>
          </thead>
          <tbody>${itensHtml}</tbody>
        </table>

        <div class="total">TOTAL: ${ultimoPedido.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
      </body>
      </html>
    `;

    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Não foi possível abrir a janela de impressão');
      return;
    }

    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const handleEmitirNfce = async (
    items: any[],
    tipoNota: 'NFe' | 'NFCe' | 'NFSe',
    clienteData: any,
    nfse?: {
      prestadorCodigoMunicipio: string;
      prestadorInscricaoMunicipal?: string;
      tomadorCodigoMunicipio: string;
      codigoMunicipioPrestacao?: string;
      itemListaServico: string;
      codigoTributarioMunicipio?: string;
      aliquota: number;
      issRetido: boolean;
      discriminacao: string;
      naturezaOperacao?: number;
      regimeEspecialTributacao?: number;
      optanteSimplesNacional?: boolean;
      incentivadorCultural?: boolean;
      baseCalculoPisCofins?: number | string;
      aliquotaPis?: number | string;
      aliquotaCofins?: number | string;
      valorPis?: number | string;
      valorCofins?: number | string;
      tipoRetencaoPisCofins?: number | string;
      valorCsll?: number | string;
      valorIrrf?: number | string;
    }
  ) => {
    if (!ultimoPedido) {
      toast.error('Nenhuma venda disponível para emitir nota');
      return;
    }

    try {
      setEmitindoNota(true);
      const valorTotal = items.reduce((acc, item) => acc + Number(item.valor_total || 0), 0);

      const payload = {
        orderCode: ultimoPedido.code,
        tipoNota,
        cliente: clienteData,
        valorTotal,
        items,
        nfse,
      };

      const result = await focusNFService.emitirNota(payload as any);
      if (result.ok) {
        toast.success('NFC-e emitida com sucesso!');
        await processarPosEmissaoNfce(ultimoPedido.code, result.data);
        setShowNfceDialog(false);
      } else {
        toast.error(result.error || 'Erro ao emitir NFC-e');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao emitir NFC-e');
    } finally {
      setEmitindoNota(false);
    }
  };

  const emitirNfceRapida = async (orderCode: string, valorTotal: number, itens: any[]) => {
    try {
      setEmitindoNota(true);

      const itensNota = (itens || []).map((item, index) => {
        const quantidade = Number(item.quantity || 1);
        const valorUnitario = Number(item.price || 0);
        const valorTotalItem = Number((valorUnitario * quantidade).toFixed(2));

        return {
          numero_item: index + 1,
          codigo_produto: String(item.id || `ITEM-${index + 1}`).slice(0, 20),
          descricao: String(item.name || `Item ${index + 1}`).slice(0, 120),
          ncm: '6204.62.00',
          cfop: '5102',
          unidade: 'UN',
          quantidade,
          valor_unitario: valorUnitario,
          valor_total: valorTotalItem,
        };
      });

      const payload = {
        orderCode,
        tipoNota: 'NFCe' as const,
        cliente: {
          nome: 'CONSUMIDOR NÃO IDENTIFICADO',
        },
        valorTotal,
        items: itensNota,
      };

      const result = await focusNFService.emitirNota(payload as any);
      if (result.ok) {
        toast.success('NFC-e balcão emitida com sucesso!');
        await processarPosEmissaoNfce(orderCode, result.data);
        return true;
      }

      toast.error(result.error || 'Falha na emissão da NFC-e balcão');
      return false;
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao emitir NFC-e balcão');
      return false;
    } finally {
      setEmitindoNota(false);
    }
  };

  const troco = metodo === 'dinheiro' ? Math.max(0, parseFloat(valorPago || '0') - total) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <PageHeader
        title="PDV - Ponto de Venda"
        description="Gerencie vendas em tempo real"
        icon={<ShoppingCart className="w-6 h-6 text-purple-600" />}
        statusIndicatorsAbove
        rightActions={
          <div className="flex gap-2">
            {!caixaAtual ? (
              <Button
                size="sm"
                onClick={() => {
                  setModoCaixa('abrir');
                  setIsDialogCaixaOpen(true);
                }}
                className="gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
              >
                🔓 Abrir Caixa
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => {
                  setModoCaixa('fechar');
                  setIsDialogCaixaOpen(true);
                }}
                className="gap-2 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
              >
                🔒 Fechar Caixa
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('/fluxo-caixa', '_blank')}
              className="gap-2 border-purple-200 hover:border-purple-400 hover:shadow-md transition-all duration-200"
              title="Abrir Fluxo de Caixa em nova aba"
            >
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline">Fluxo de Caixa</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/pedidos')}
              className="gap-2 border-purple-200 hover:border-purple-400 hover:shadow-md transition-all duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Voltar</span>
            </Button>
          </div>
        }
      />

      {/* Conteúdo */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-4 md:py-6">
        <div className="mb-4 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-4 md:px-5 py-2.5 md:py-3 text-xs md:text-sm text-amber-900 shadow-md hover:shadow-lg transition-all duration-300 animate-fade-in">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="font-medium">
              ⚠️ Para emitir cupom fiscal NFC-e no PDV, primeiro preencha as configurações em
              <strong> Config. Notas Fiscais</strong>.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="border-amber-300 text-amber-900 hover:bg-amber-100 transition-all duration-200"
              onClick={() => navigate('/configuracao-focusnf')}
            >
              Ir para Configuração
            </Button>
          </div>
        </div>
        <Tabs defaultValue="nova-venda" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2 bg-gray-100 border border-purple-200 shadow-md p-1">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-purple-700 data-[state=active]:text-white transition-all duration-300">📊 Dashboard</TabsTrigger>
            <TabsTrigger value="nova-venda" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-purple-600 data-[state=active]:text-white transition-all duration-300">💳 Nova Venda</TabsTrigger>
          </TabsList>

          {/* Dashboard */}
          <TabsContent value="dashboard">
            <Card className="bg-gradient-to-br from-white to-purple-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden">
              <CardHeader className="bg-purple-50 border-b border-gray-200 px-4 md:px-6 py-3">
                <CardTitle className="text-base md:text-lg text-gray-900">📊 Dashboard PDV - Hoje</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-5 space-y-4 md:space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                  <Card className="bg-gradient-to-br from-white to-purple-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.1s'}}>
                    <CardContent className="pt-3 md:pt-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Vendas Totais</p>
                      <p className="text-lg md:text-xl font-bold text-purple-600 mt-1">{dashboardData.vendas.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-white to-pink-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.2s'}}>
                    <CardContent className="pt-3 md:pt-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Itens Vendidos</p>
                      <p className="text-lg md:text-xl font-bold text-blue-600 mt-1">{dashboardData.itens}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-white to-green-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.3s'}}>
                    <CardContent className="pt-3 md:pt-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">NFC-e Emitidas</p>
                      <p className="text-lg md:text-xl font-bold text-green-600 mt-1">{dashboardData.nfce}</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                  <Card className="bg-gradient-to-br from-white to-cyan-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.4s'}}>
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-start gap-2">
                        <div className="p-2 bg-cyan-50 rounded-full flex-shrink-0">
                          <DollarSign className="w-5 h-5 text-cyan-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Saldo</p>
                          <p className={`text-base md:text-lg font-bold mt-0.5 ${saldoCaixa >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                            {formatCurrency({ value: saldoCaixa, currency: 'BRL' })}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-white to-orange-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.5s'}}>
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-start gap-2">
                        <div className="p-2 bg-orange-50 rounded-full flex-shrink-0">
                          <CreditCard className="w-5 h-5 text-orange-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Pagamentos</p>
                          <div className="space-y-0.5 text-xs">
                            <p className="text-gray-700"><span className="font-medium">🟢 PIX:</span> {formatCurrency({ value: pagamentosPorMetodo.pix, currency: 'BRL' })}</p>
                            <p className="text-gray-700"><span className="font-medium">💵 Dinheiro:</span> {formatCurrency({ value: pagamentosPorMetodo.dinheiro, currency: 'BRL' })}</p>
                            <p className="text-gray-700"><span className="font-medium">💳 Cartão:</span> {formatCurrency({ value: pagamentosPorMetodo.cartao, currency: 'BRL' })}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className={`bg-gradient-to-br ${Math.abs(saldoCaixa - saldoFluxo) < 0.01 ? 'from-white to-emerald-50 border-gray-200' : 'from-white to-amber-50 border-gray-200'} shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden`} style={{animationDelay: '0.6s'}}>
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-start gap-2">
                        <div className={`p-2 rounded-full flex-shrink-0 ${Math.abs(saldoCaixa - saldoFluxo) < 0.01 ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                          {Math.abs(saldoCaixa - saldoFluxo) < 0.01 ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Reconciliação</p>
                          <p className={`text-base md:text-lg font-bold mt-0.5 ${Math.abs(saldoCaixa - saldoFluxo) < 0.01 ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {Math.abs(saldoCaixa - saldoFluxo) < 0.01 ? 'Sincr.' : 'Verificar'}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">Dif: {formatCurrency({ value: saldoCaixa - saldoFluxo, currency: 'BRL' })}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                {dashboardData.vendas === 0 && (
                  <p className="text-center text-gray-500 text-sm py-4">Nenhuma venda registrada hoje</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Nova Venda */}
          <TabsContent value="nova-venda">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
              {/* Carrinho */}
              <div className="lg:col-span-2">
                <Card className="bg-gradient-to-br from-white to-purple-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.1s'}}>
                  <CardHeader className="bg-purple-50 border-b border-gray-200 px-4 md:px-5 py-3">
                    <CardTitle className="flex items-center gap-2 text-base md:text-lg text-gray-900">
                      <ShoppingCart className="w-5 h-5 text-purple-600" />
                      Carrinho
                      <span className="ml-auto text-xs md:text-sm bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold">
                        {carrinho.length}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 md:p-6">
                    <div className="space-y-6">
                    {/* Buscar Produto do Catálogo */}
                    <div className="space-y-2">
                      <Label className="text-xs md:text-sm font-semibold text-gray-700 uppercase tracking-wide">Buscar no Catálogo</Label>
                      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            className="w-full justify-between"
                          >
                            {selectedProduct
                              ? products.find(p => p.id === selectedProduct)?.name
                              : 'Selecione um produto...'}
                            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>

                        <PopoverContent className="w-[430px] p-0" align="start">
                          <Command>
                            <CommandInput
                              placeholder="Buscar produto..."
                              value={searchTerm}
                              onValueChange={setSearchTerm}
                            />
                            <CommandList>
                              <CommandEmpty>Nenhum produto encontrado</CommandEmpty>
                              <CommandGroup>
                                {filteredProducts.map(p => (
                                  <CommandItem
                                    key={p.id}
                                    value={`${p.id} ${p.name}`}
                                    onSelect={() => {
                                      setSelectedProduct(p.id);
                                      handleAdicionarProduto(p.id);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        'mr-2 h-4 w-4',
                                        selectedProduct === p.id ? 'opacity-100' : 'opacity-0'
                                      )}
                                    />
                                    <div className="flex-1">
                                      <div className="text-sm font-medium">{p.name}</div>
                                      <div className="text-xs text-gray-500">
                                        {Number(p.unit_price || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                      </div>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Adicionar Customizado */}
                    <div className="space-y-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 shadow-md hover:shadow-lg transition-all duration-300">
                      <p className="text-xs md:text-sm font-semibold text-gray-700 uppercase tracking-wide">Adicionar Produto Personalizado</p>
                      <Input
                        placeholder="Nome do produto"
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        className="border-purple-200 focus:border-purple-500 focus:ring-purple-500"
                      />
                      <div className="flex gap-2">
                        <Input
                          placeholder="Preço (R$)"
                          type="number"
                          step="0.01"
                          min="0"
                          value={customPrice}
                          onChange={(e) => setCustomPrice(e.target.value)}
                          className="border-purple-200 focus:border-purple-500 focus:ring-purple-500"
                        />
                        <Button
                          onClick={handleAdicionarCustomizado}
                          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold whitespace-nowrap shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Adicionar
                        </Button>
                      </div>
                    </div>

                    {/* Produtos no Carrinho */}
                    <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                      {carrinho.length === 0 ? (
                        <p className="text-center text-gray-500 py-8 text-sm">🛒 Carrinho vazio</p>
                      ) : (
                        carrinho.map((p, index) => (
                          <div key={p.id} className="p-3 md:p-2.5 bg-gradient-to-r from-purple-50 to-pink-50 border-l-3 border-purple-500 rounded-lg hover:shadow-md transition-all duration-200 animate-fade-in" style={{animationDelay: `${0.1 + index * 0.05}s`}}>
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <p className="font-semibold text-sm text-gray-900">{p.name}</p>
                                <p className="text-xs text-gray-600 mt-0.5">{p.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemover(p.id)}
                                className="h-7 w-7 text-red-600 hover:bg-red-50 flex-shrink-0 ml-2"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAtualizarQtd(p.id, p.quantity - 1)}
                                className="h-7 px-2 text-xs"
                              >
                                −
                              </Button>
                              <Input
                                type="number"
                                min="1"
                                value={p.quantity}
                                onChange={(e) => handleAtualizarQtd(p.id, parseInt(e.target.value) || 1)}
                                className="w-10 h-7 text-center text-xs"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAtualizarQtd(p.id, p.quantity + 1)}
                                className="h-7 px-2 text-xs"
                              >
                                +
                              </Button>
                              <span className="ml-auto font-bold text-purple-600 text-sm">
                                {(p.price * p.quantity).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Total */}
                    {carrinho.length > 0 && (
                      <div className="border-t-2 border-purple-200 pt-4 bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl shadow-md">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-gray-900">Total:</span>
                          <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                            {total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </span>
                        </div>
                      </div>
                    )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Pagamento */}
              <div>
                <Card className="bg-gradient-to-br from-white to-pink-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in h-full overflow-hidden" style={{animationDelay: '0.2s'}}>
                  <CardHeader className="bg-pink-50 border-b border-gray-200 px-4 md:px-5 py-3">
                    <CardTitle className="text-base md:text-lg text-gray-900">💳 Pagamento</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 md:p-5 space-y-3 md:space-y-4">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-3 md:p-4 rounded-lg shadow-sm">
                      <p className="text-xs opacity-90 uppercase tracking-wide font-semibold">Total da Venda</p>
                      <p className="text-xl md:text-2xl font-bold mt-1">{total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs md:text-sm font-semibold text-gray-700 uppercase tracking-wide">Método de Pagamento</Label>
                      <select
                        value={metodo}
                        onChange={(e) => setMetodo(e.target.value as any)}
                        className="w-full border border-purple-200 rounded-lg p-2.5 text-sm font-medium focus:border-purple-500 focus:ring-2 focus:ring-purple-300/30 transition-all duration-200"
                      >
                        <option value="pix">🟢 PIX</option>
                        <option value="cartao">🔵 Cartão</option>
                        <option value="dinheiro">💵 Dinheiro</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs md:text-sm font-semibold text-gray-700 uppercase tracking-wide">Valor Pago (R$)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={valorPago}
                        onChange={(e) => setValorPago(e.target.value)}
                        disabled={metodo !== 'dinheiro'}
                        className="border-purple-200 focus:border-purple-500 focus:ring-purple-500"
                      />
                    </div>

                    <div className="rounded-xl border border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 p-4 shadow-md">
                      <label className="flex items-center gap-3 text-xs md:text-sm text-purple-900 cursor-pointer hover:text-purple-700 transition-colors">
                        <input
                          type="checkbox"
                          checked={emitirNfceAutomaticamente}
                          onChange={(e) => setEmitirNfceAutomaticamente(e.target.checked)}
                          className="w-4 h-4 cursor-pointer accent-purple-600"
                        />
                        <span className="font-medium">Emitir NFC-e automaticamente</span>
                      </label>
                      <label className="flex items-center gap-3 text-xs md:text-sm text-purple-900 cursor-pointer hover:text-purple-700 transition-colors mt-3">
                        <input
                          type="checkbox"
                          checked={usarNfceRapida}
                          onChange={(e) => setUsarNfceRapida(e.target.checked)}
                          className="w-4 h-4 cursor-pointer accent-purple-600"
                        />
                        <span className="font-medium">Usar NFC-e balcão</span>
                      </label>
                      <p className="text-xs text-purple-700 mt-2 ml-7">
                        Envia direto para a Sefaz sem exigir nome/CPF.
                      </p>
                    </div>

                    {metodo === 'dinheiro' && (
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-xl border border-amber-200 shadow-md hover:shadow-lg transition-all duration-300 animate-fade-in">
                        <p className="text-sm md:text-base text-amber-900">
                          💵 <span className="font-semibold">Troco: </span><strong className="text-lg">{troco.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
                        </p>
                      </div>
                    )}

                    <Button
                      onClick={() => {
                        if (carrinho.length === 0) {
                          toast.error('Carrinho vazio');
                          return;
                        }
                        setShowDialog(true);
                      }}
                      className="w-full h-12 md:h-14 text-base md:text-lg font-bold bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-95 rounded-xl"
                    >
                      ✓ Confirmar Pagamento
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog de Confirmação */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Venda</DialogTitle>
            <DialogDescription>Revise os dados antes de finalizar</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="bg-gray-50 p-3 rounded space-y-1">
              <p className="font-semibold text-sm">Produtos:</p>
              {carrinho.map((p, i) => (
                <p key={i} className="text-xs text-gray-600">
                  {p.name} · {p.quantity}x · {(p.price * p.quantity).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </p>
              ))}
            </div>

            <div className="border-t pt-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Total:</span>
                <span className="font-semibold">{total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Método:</span>
                <span className="font-semibold">{metodo.toUpperCase()}</span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={isProcessing}>
              Cancelar
            </Button>
            <Button
              onClick={handleFinalizarVenda}
              disabled={isProcessing}
              className="bg-purple-500 hover:bg-purple-600 text-white font-semibold"
            >
              {isProcessing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isProcessing ? 'Processando...' : 'Finalizar Venda'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog pós-venda para ações rápidas */}
      <Dialog open={showReciboDialog} onOpenChange={setShowReciboDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Venda finalizada com sucesso</DialogTitle>
            <DialogDescription>
              O que você deseja fazer agora?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 text-sm">
            <p><strong>Código:</strong> {ultimoPedido?.code}</p>
            <p><strong>Total:</strong> {ultimoPedido?.total?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
          </div>

          <DialogFooter className="gap-2 flex-wrap justify-start">
            <Button variant="outline" onClick={() => setShowReciboDialog(false)}>
              Fechar
            </Button>
            <Button variant="outline" onClick={imprimirRecibo}>
              Imprimir Recibo
            </Button>
            <Button variant="outline" onClick={imprimirDanfeUltimaVenda}>
              Imprimir DANFE NFC-e
            </Button>
            <Button
              variant="outline"
              disabled={emitindoNota || !ultimoPedido}
              onClick={async () => {
                if (!ultimoPedido) return;
                const ok = await emitirNfceRapida(ultimoPedido.code, ultimoPedido.total, ultimosItensVenda);
                if (ok) setShowReciboDialog(false);
              }}
            >
              {emitindoNota ? 'Emitindo...' : 'NFC-e Balcão'}
            </Button>
            <Button 
              onClick={() => {
                setShowReciboDialog(false);
                setShowNfceDialog(true);
              }}
              className="min-w-max"
            >
              Nota Fiscal Completa (NFe/NFCe/NFSe)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de emissão de nota fiscal */}
      <DialogEmitirNota
        open={showNfceDialog}
        onOpenChange={setShowNfceDialog}
        onEmitir={handleEmitirNfce}
        valorTotalPadrao={ultimoPedido?.total || 0}
        orderCode={ultimoPedido?.code || ''}
        loading={emitindoNota}
        descricaoPedido={ultimoPedido ? `Venda PDV ${ultimoPedido.code}` : ''}
      />

      {/* Dialog de Abrir/Fechar Caixa */}
      <DialogAbrirFecharCaixa
        isOpen={isDialogCaixaOpen}
        onClose={() => setIsDialogCaixaOpen(false)}
        mode={modoCaixa}
        caixaAtual={caixaAtual}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['pdv-caixa-aberto'] })}
      />

      {/* Dialog de Seleção de Forma e Conta */}
      <DialogSelecionarFormaEConta
        isOpen={showSelecionarFormaModal}
        total={total}
        onConfirm={handleConfirmarVendaComFormaEConta}
        onCancel={() => setShowSelecionarFormaModal(false)}
      />
    </div>
  );
}
